import React from 'react'

import { getIsForInAppPresentation, routes } from '../../routes'
import { useFlag } from '../../flags'
import { useGetSocialSignIn } from '../../libs/authentication'
import {
  Container,
  Divider,
  ButtonContainer,
  StyledLinkButton,
} from './SocialSignIn.styles'
import { Location } from 'history'
import { useLocation } from 'react-router'
import { ContinueWithApple } from './ContinueWithApple'

interface LocationState {
  from?: Location
  // In some cases, we need to force the users to login with email and password
  // for their SSO account, for example, when accepting a workspace invitation
  hideSSO?: boolean
}

interface SocialSignInProps {
  /**
   * Optional redirect destination to override the default behavior.
   * When provided, this will be used as the destinationUrl for social sign-in
   * instead of deriving it from location.state?.from
   *
   * This is used in the AcceptInvite flow to ensure the user is redirected
   * back to the invite acceptance page after signing in.
   *
   * If not provided, the user will be redirected to their original destination
   * or the default entry point after signing in.
   *
   */
  redirectTo?: string
  hideSSO?: LocationState['hideSSO']
}

const getDestinationUrl = (redirectTo?: string, from?: Location | string) => {
  if (redirectTo) {
    return redirectTo
  }

  if (!from) {
    return null
  }

  // "AcceptProjectInviteByTokenView" passes a string to "from"
  // and not a location object
  if (typeof from === 'string') {
    return from
  }

  // Build URL from Location object
  const { pathname = '', search = '', hash = '' } = from

  return `${pathname}${search}${hash}`
}

export const SocialSignIn = ({ redirectTo, hideSSO }: SocialSignInProps) => {
  // SWEB-1921: Remove the social-auth FF
  // https://linear.app/sketch/issue/SWEB-1921/remove-the-social-auth-ff
  const isSocialAuthEnabled = useFlag('social-auth')

  const location = useLocation<LocationState>()
  const { state } = location

  const { getSocialSigninStartUrl, loading } = useGetSocialSignIn()

  const showSSOSignInButton = !state?.hideSSO && !hideSSO
  const isForInAppPresentation = getIsForInAppPresentation()

  // Don't render if its for in-app presentation.
  // We cannot show any sign up options in the Mac App
  if (!isSocialAuthEnabled || isForInAppPresentation) {
    return null
  }

  const handleAppleSignIn = () => {
    const destinationUrl = getDestinationUrl(redirectTo, state?.from)

    getSocialSigninStartUrl({
      variables: {
        provider: 'APPLE',
        destinationUrl,
      },
    })
  }

  return (
    <Container data-testid="social-sign-in">
      <ButtonContainer>
        <ContinueWithApple
          showIconOnly={showSSOSignInButton}
          onClick={handleAppleSignIn}
          loading={loading}
        />
        {showSSOSignInButton && (
          <StyledLinkButton
            size="40"
            variant="secondary"
            to={{
              pathname: routes.SSO_SIGN_IN.create({}),
              state: {
                from: location.state?.from,
              },
            }}
          >
            SSO
          </StyledLinkButton>
        )}
      </ButtonContainer>

      <Divider>or</Divider>
    </Container>
  )
}
